import ThumbsUp from "@redotech/redo-icons/thumbs-up.svg";
import {
  CommentState,
  ConversationPlatform,
  ExpandedConversation,
  ExpandedConversationMessage,
} from "@redotech/redo-model/conversation";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import CircleSpinner from "@redotech/redo-web/circle-spinner.svg";
import { Flex } from "@redotech/redo-web/flex";
import MeatballIcon from "@redotech/redo-web/icon-old/meatballs.svg";
import { ExternalLink } from "@redotech/redo-web/link";
import { Text } from "@redotech/redo-web/text";
import { formatDateTime } from "@redotech/redo-web/time";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { formatTimeAgo } from "@redotech/util/date-format";
import "quill/dist/quill.snow.css";
import {
  Fragment,
  memo,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import * as messageCss from "../message.module.css";
import * as styles from "../meta/meta-action-menu.module.css";
import { MessageBadges } from "./message-badges";

export const MessageInformation = memo(function MessageInformation({
  message,
  conversation,
  senderName,
  setActionMenuAnchor,
  isActionLoading,
}: {
  message: ExpandedConversationMessage;
  conversation: ExpandedConversation;
  senderName: string | undefined;
  setActionMenuAnchor: React.Dispatch<SetStateAction<HTMLElement | null>>;
  isActionLoading: boolean;
}) {
  const senderIdentifiers: ReactNode[] = [];
  if (senderName) {
    senderIdentifiers.push(<span>{senderName}</span>);
  }
  if (
    message.type === "customer" &&
    [
      ConversationPlatform.INSTAGRAM,
      ConversationPlatform.INSTAGRAM_COMMENTS,
    ].includes(conversation.platform) &&
    message.instagram?.comment?.username
  ) {
    const instagramUrl = `https://instagram.com/${encodeURIComponent(message.instagram.comment.username)}`;
    senderIdentifiers.push(
      <ExternalLink
        className={messageCss.username}
        showIcon={false}
        url={instagramUrl}
      >
        @{message.instagram.comment.username}
      </ExternalLink>,
    );
  }
  if (
    message.type === "customer" &&
    conversation.platform === ConversationPlatform.FACEBOOK_COMMENTS &&
    message.facebook?.comment?.facebookName &&
    !senderIdentifiers.length
  ) {
    senderIdentifiers.push(
      <span>{message.facebook.comment.facebookName}</span>,
    );
  }

  const [timeSinceMessage, setTimeSinceMessage] = useState<string>(
    formatTimeAgo(message.sentAt),
  );

  useEffect(() => {
    setTimeSinceMessage(formatTimeAgo(message.sentAt));
    const intervalId = setInterval(() => {
      setTimeSinceMessage(formatTimeAgo(message.sentAt));
    }, 60 * 1000);
    return () => clearInterval(intervalId);
  }, [message.sentAt]);

  const messageState =
    conversation.platform === ConversationPlatform.INSTAGRAM_COMMENTS
      ? message.instagram?.comment?.state
      : message.facebook?.comment?.state;

  const hasCommentBeenLiked =
    conversation.platform === ConversationPlatform.INSTAGRAM_COMMENTS
      ? message.instagram?.comment?.liked
      : message.facebook?.comment?.liked;

  return (
    <div className={messageCss.infoAbove}>
      <Flex>
        <Flex justify="space-between" w="full">
          <div
            className={
              message.type === "merchant"
                ? messageCss.senderDate
                : messageCss.senderDateCustomer
            }
          >
            <Flex align="baseline">
              {senderIdentifiers.length > 0 && (
                <>
                  {senderIdentifiers.map((identifier, index) => (
                    <>
                      {index > 0 && " - "}
                      <Fragment key={index}>
                        <Text fontSize="sm" fontWeight="medium">
                          {identifier}
                        </Text>
                      </Fragment>
                    </>
                  ))}
                </>
              )}
              <Tooltip
                arrow
                placement="top"
                title={formatDateTime(Temporal.Instant.from(message.sentAt))}
              >
                {/* This empty div needs to be here for the tooltip to work.
                  Removing it breaks the tooltip and causes error logs when
                  running locally that say "The children component of the
                  Tooltip is not forwarding its props correctly." Not sure
                  what that means, but something about our Text component
                  doesn't play well with Tooltip. Feel free to look for a
                  better way than this. -Zach */}
                <div>
                  <Text fontSize="xs" fontWeight="regular">
                    {timeSinceMessage}
                  </Text>
                </div>
              </Tooltip>
            </Flex>
          </div>

          <Flex align="center" justify="center">
            <MessageBadges conversation={conversation} message={message} />
            {hasCommentBeenLiked && (
              <ThumbsUp className={styles.menuIconInline} />
            )}
            {[
              ConversationPlatform.INSTAGRAM_COMMENTS,
              ConversationPlatform.FACEBOOK_COMMENTS,
            ].includes(conversation.platform) &&
              messageState !== CommentState.DELETED && (
                <RedoButton
                  hierarchy="tertiary"
                  IconLeading={isActionLoading ? CircleSpinner : MeatballIcon}
                  onClick={(e) => {
                    if (!isActionLoading) {
                      setActionMenuAnchor?.(e.currentTarget);
                    }
                  }}
                  pending={isActionLoading}
                  size="xs"
                />
              )}
          </Flex>
        </Flex>
      </Flex>

      {!!message.instagram?.repliedStoryUrl && (
        <div>
          Replied to your{" "}
          <ExternalLink
            showIcon={false}
            url={message.instagram.repliedStoryUrl}
          >
            story
          </ExternalLink>
        </div>
      )}
      {!!message.instagram?.comment &&
        conversation.platform === ConversationPlatform.INSTAGRAM && (
          <div>Commented on your post</div>
        )}
      {((message.instagram?.privateRepliedComment &&
        Object.keys(message.instagram.privateRepliedComment).length > 0) ||
        (message.facebook?.privateRepliedComment &&
          Object.keys(message.facebook.privateRepliedComment).length > 0)) && (
        <div>
          Private reply to their comment on your{" "}
          {message.instagram?.privateRepliedComment?.permalink ? (
            <ExternalLink
              showIcon={false}
              url={message.instagram.privateRepliedComment.permalink}
            >
              post
            </ExternalLink>
          ) : message.facebook?.privateRepliedComment?.permalink ? (
            <ExternalLink
              showIcon={false}
              url={message.facebook.privateRepliedComment.permalink}
            >
              post
            </ExternalLink>
          ) : (
            "post"
          )}
        </div>
      )}
    </div>
  );
});
